import { useState, useCallback, useEffect, useRef } from 'react'
import Map, {
  Layer,
  Marker,
  Source,
  MapLayerMouseEvent,
  MapRef,
} from 'react-map-gl'
import { Loader } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import 'mapbox-gl/dist/mapbox-gl.css'

type Point = [number, number] // [lon, lat]
type Bounds = [[number, number], [number, number]] // [[lon, lat], [lon, lat]]
type Mode = 'route' | 'reroute'

interface RouteLocation {
  gnss_utc_time: string | null
  sent_at: string | null
  created_at: string
  is_buffered: boolean
  speed: number | null
  azimuth: number | null
  altitude: number | null
  location: {
    type: 'Point'
    coordinates: Point
  }
}

interface Snapped {
  point: Point
  distance_m: number
}

interface RouteResponse {
  locations: RouteLocation[]
  total_distance_m: number
  average_speed_kmh: number | null
  waypoints: number
  buffer_m: number
  region?: string
  snapped?: { start: Snapped | null; end: Snapped | null }
}

interface RerouteResponse extends RouteResponse {
  rerouted: boolean
  start: {
    raw: Point
    used: Point
    method: 'sea' | 'snapped' | 'anchor'
    heading_deg: number | null
    anchor: Point | null
    visible_from_anchor: boolean | null
  }
  off_route_distance_m: number | null
  eta_seconds: number | null
}

/** A /reroute answer and how many track fixes existed when it was requested. */
interface RerouteAnswer {
  fixCount: number
  response: RerouteResponse
}

interface Override {
  type: 'Feature'
  id: string
  properties: { name: string; region?: string }
  geometry: { type: string; coordinates: number[][][] }
}

interface HealthRegion {
  name: string
  bbox: [number, number, number, number]
  mesh_triangles: number
  ready: boolean
  rebuilding: boolean
}

const TRACK_FIXES_SENT = 10 // /reroute gets the most recent fixes only
const FIX_INTERVAL_MS = 60_000 // synthetic spacing between clicked fixes

const boundsFor = (region: HealthRegion): Bounds => [
  [region.bbox[0], region.bbox[1]],
  [region.bbox[2], region.bbox[3]],
]

const coordsOf = (r: RouteResponse): Point[] =>
  r.locations.map((loc) => loc.location.coordinates)

const samePoint = (a: Point, b: Point) => a[0] === b[0] && a[1] === b[1]

/** True when locations[0] -> locations[1] is a land or snap-to-route leg. */
const hasFirstLeg = (r: RouteResponse | RerouteResponse): boolean => {
  const pts = coordsOf(r)
  if (pts.length < 2) return false
  if (r.snapped?.start) return true
  const start = (r as RerouteResponse).start
  return !!start && !samePoint(start.raw, pts[1])
}

/** The sea route only: without the land legs at either end. */
const seaCoords = (r: RouteResponse | RerouteResponse): Point[] => {
  const pts = coordsOf(r)
  const from = hasFirstLeg(r) ? 1 : 0
  const to = r.snapped?.end ? pts.length - 1 : pts.length
  return pts.slice(from, to)
}

const km = (m: number) => `${(m / 1000).toFixed(1)} km`

const minutes = (s: number | null) =>
  s == null ? '–' : s < 90 ? `${Math.round(s)} s` : `${Math.round(s / 60)} min`

const snapText = (r: RouteResponse) => {
  const parts = []
  if (r.snapped?.start)
    parts.push(`start snapped ${r.snapped.start.distance_m.toFixed(0)} m`)
  if (r.snapped?.end)
    parts.push(`end snapped ${r.snapped.end.distance_m.toFixed(0)} m`)
  return parts.length ? `, ${parts.join(', ')}` : ''
}

const rerouteText = (n: number, r: RerouteResponse) => {
  const s = r.start
  const where =
    s.method === 'sea'
      ? 'fix at sea'
      : s.method === 'snapped'
        ? `fix on land, snapped ${r.snapped?.start?.distance_m.toFixed(0) ?? '?'} m` +
          (s.visible_from_anchor === true
            ? ' (visible from last sea fix)'
            : s.visible_from_anchor === false
              ? ' (heading/nearest fallback)'
              : '')
        : 'fix on land, using last sea fix'
  const heading =
    s.heading_deg == null
      ? 'no heading'
      : `heading ${s.heading_deg.toFixed(0)}°`
  const decision =
    r.off_route_distance_m == null
      ? 'fresh route'
      : r.rerouted
        ? `off route by ${r.off_route_distance_m.toFixed(0)} m → rerouted`
        : `on route (${r.off_route_distance_m.toFixed(0)} m) → kept previous route`
  return `Fix #${n}: ${where} · ${heading} · ${decision} · ${km(r.total_distance_m)} · ETA ${minutes(r.eta_seconds)}`
}

const buttonStyle = (active = false, primary = false): React.CSSProperties => ({
  backgroundColor: primary ? '#3b82f6' : active ? '#1f2937' : 'white',
  color: primary || active ? 'white' : '#333',
  border: primary || active ? 'none' : '1px solid #ccc',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 14,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
})

const inputStyle: React.CSSProperties = {
  width: 64,
  padding: '4px 6px',
  fontSize: 13,
  border: '1px solid #ccc',
  borderRadius: 6,
}

export default function RoutingPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('route')
  // Route mode
  const [start, setStart] = useState<Point | null>(null)
  const [routeData, setRouteData] = useState<RouteResponse | null>(null)
  // Shared: destination
  const [end, setEnd] = useState<Point | null>(null)
  // Reroute mode: the boat's track (clicked GPS fixes, oldest first), the
  // answers received so far, and the route carried over from Route mode
  // (drawn grey, sent as the first previous_route)
  const [fixes, setFixes] = useState<Point[]>([])
  const [answers, setAnswers] = useState<RerouteAnswer[]>([])
  const [baseRoute, setBaseRoute] = useState<RouteResponse | null>(null)
  // Boat Track on: clicks only record fixes; "Reroute now" sends them
  const [recording, setRecording] = useState(false)
  const [speedKmh, setSpeedKmh] = useState('30')
  const [snapM, setSnapM] = useState('') // blank = server default
  const [offRouteM, setOffRouteM] = useState('100')
  const [usePrevious, setUsePrevious] = useState(true)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<Override[]>([])
  const [regions, setRegions] = useState<HealthRegion[]>([])
  const mapRef = useRef<MapRef>(null)

  useEffect(() => {
    fetch('/routing/sea-overrides')
      .then((res) => res.json())
      .then((data) => setOverrides(data.overrides ?? []))
      .catch(() => {})
  }, [])

  // Center the map on the routing service's first region
  useEffect(() => {
    fetch('/routing/health')
      .then((res) => res.json())
      .then((data) => setRegions(data.regions ?? []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (regions.length > 0) {
      mapRef.current?.fitBounds(boundsFor(regions[0]), {
        padding: 40,
        duration: 0,
      })
    }
  }, [regions])

  const reset = useCallback(() => {
    setStart(null)
    setEnd(null)
    setRouteData(null)
    setFixes([])
    setAnswers([])
    setBaseRoute(null)
    setError(null)
  }, [])

  const switchMode = useCallback(
    (next: Mode) => {
      if (next === 'reroute' && mode === 'route' && routeData && start) {
        // Keep the route just computed as the previous route (grey) and the
        // destination; its start is the boat's first fix.
        setBaseRoute(routeData)
        setFixes([start])
        setAnswers([])
        setRouteData(null)
        setStart(null)
        setError(null)
      } else {
        reset()
      }
      setMode(next)
    },
    [mode, routeData, start, reset]
  )

  const postJson = useCallback(async (url: string, body: unknown) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({ detail: res.statusText }))
      const detail = data.detail
      throw new Error(
        typeof detail === 'string'
          ? detail
          : JSON.stringify(detail ?? res.statusText)
      )
    }
    return res.json()
  }, [])

  const snapField = useCallback(
    () => (snapM.trim() === '' ? {} : { snap_m: Number(snapM) }),
    [snapM]
  )

  const fetchRoute = useCallback(
    async (s: Point, e: Point) => {
      setLoading(true)
      setError(null)
      try {
        const now = new Date()
        const fourHoursLater = new Date(now.getTime() + 4 * 60 * 60 * 1000)
        const data: RouteResponse = await postJson('/routing/route', {
          start_lon: s[0],
          start_lat: s[1],
          end_lon: e[0],
          end_lat: e[1],
          start_time: now.toISOString(),
          end_time: fourHoursLater.toISOString(),
          ...snapField(),
        })
        setRouteData(data)
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [postJson, snapField]
  )

  /** Send the track (its most recent fixes) and the current route to /reroute. */
  const sendReroute = useCallback(
    async (track: Point[], e: Point, previous: RerouteAnswer[]) => {
      setLoading(true)
      setError(null)
      try {
        const sent = track.slice(-TRACK_FIXES_SENT)
        const base = Date.now() - (sent.length - 1) * FIX_INTERVAL_MS
        const last = previous[previous.length - 1]
        const previousRoute = last
          ? seaCoords(last.response)
          : baseRoute
            ? seaCoords(baseRoute)
            : null
        const data: RerouteResponse = await postJson('/routing/reroute', {
          track: sent.map((p, i) => ({
            lon: p[0],
            lat: p[1],
            time: new Date(base + i * FIX_INTERVAL_MS).toISOString(),
          })),
          end_lon: e[0],
          end_lat: e[1],
          previous_route: usePrevious ? previousRoute : null,
          speed_kmh: Number(speedKmh) > 0 ? Number(speedKmh) : null,
          off_route_m: Number(offRouteM) >= 0 ? Number(offRouteM) : 100,
          ...snapField(),
        })
        setAnswers([...previous, { fixCount: track.length, response: data }])
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [postJson, usePrevious, speedKmh, offRouteM, snapField, baseRoute]
  )

  /** Drop the last fix, and any answer that was computed with it. */
  const undo = useCallback(() => {
    setError(null)
    const remaining = fixes.slice(0, -1)
    setFixes(remaining)
    setAnswers((prev) => prev.filter((a) => a.fixCount <= remaining.length))
  }, [fixes])

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (mode === 'reroute' && ev.key === 'z' && (ev.ctrlKey || ev.metaKey)) {
        ev.preventDefault()
        undo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, undo])

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const point: Point = [e.lngLat.lng, e.lngLat.lat]
      if (loading) return

      if (mode === 'reroute') {
        // 1st click: destination; every further click: a new GPS fix
        if (!end) {
          setError(null)
          setEnd(point)
          return
        }
        const track = [...fixes, point]
        setFixes(track)
        if (!recording) sendReroute(track, end, answers)
        return
      }

      if (routeData || error) {
        reset()
        setStart(point)
        return
      }
      if (!start) {
        setStart(point)
      } else {
        setEnd(point)
        fetchRoute(start, point)
      }
    },
    [
      mode,
      end,
      fixes,
      answers,
      recording,
      start,
      routeData,
      error,
      loading,
      reset,
      fetchRoute,
      sendReroute,
    ]
  )

  const lastAnswer = answers[answers.length - 1]
  const prevAnswer = answers[answers.length - 2]
  const unsent = fixes.length - (lastAnswer?.fixCount ?? 0)

  const statusText = loading
    ? mode === 'reroute'
      ? 'Rerouting...'
      : 'Calculating route...'
    : error
      ? `Error: ${error}. ${mode === 'reroute' ? 'Click another position or Ctrl+Z.' : 'Click to start over.'}`
      : mode === 'reroute'
        ? !end
          ? 'Reroute mode: click the DESTINATION'
          : fixes.length === 0
            ? recording
              ? 'Boat Track on: click to record the boat’s fixes, then press Reroute now.'
              : 'Click where the boat is. Every further click is its next GPS fix.'
            : unsent > 0
              ? `${fixes.length} fix${fixes.length === 1 ? '' : 'es'} in the track, ${unsent} not sent yet. Press Reroute now (last ${TRACK_FIXES_SENT} are sent)${baseRoute && !lastAnswer ? '; the grey route is the previous route' : ''}.`
              : `${rerouteText(fixes.length, lastAnswer.response)}. Click the next fix, Ctrl+Z undo.`
        : routeData
          ? `Route${routeData.region ? ` (${routeData.region})` : ''}: ${routeData.waypoints} waypoints, ${km(routeData.total_distance_m)}, ${routeData.average_speed_kmh?.toFixed(1) ?? '–'} km/h avg${snapText(routeData)}. Click to start over.`
          : start
            ? 'Click to set end point'
            : 'Click to set start point'

  // Everything drawn as one FeatureCollection, styled per `kind`
  const features: GeoJSON.Feature[] = []
  const line = (points: Point[], kind: string) => {
    if (points.length >= 2)
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: points },
        properties: { kind },
      })
  }
  const current: RouteResponse | RerouteResponse | null =
    mode === 'reroute' ? (lastAnswer?.response ?? null) : routeData
  if (current) {
    const pts = coordsOf(current)
    const kept =
      mode === 'reroute' && (current as RerouteResponse).rerouted === false
    line(seaCoords(current), kept ? 'kept' : 'route')
    if (hasFirstLeg(current)) line(pts.slice(0, 2), 'land')
    if (current.snapped?.end) line(pts.slice(-2), 'land')
  }
  if (mode === 'reroute') {
    // The route being replaced: the carried-over route before any answer, or
    // the previous answer once a reroute happened
    const previous: RouteResponse | null = !lastAnswer
      ? baseRoute
      : lastAnswer.response.rerouted
        ? (prevAnswer?.response ?? baseRoute)
        : null
    if (previous) line(seaCoords(previous), 'previous')
    line(fixes, 'track')
    fixes.forEach((fix, i) =>
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: fix },
        properties: { kind: 'fix', sent: i < (lastAnswer?.fixCount ?? 0) },
      })
    )
  }
  const drawing: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features,
  }
  const kindIs = (kind: string): any => ['==', ['get', 'kind'], kind]

  const boat = fixes[fixes.length - 1] ?? null

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken="pk.eyJ1IjoiaGJhbmRvdiIsImEiOiJjbHVoMTZqazEya2p5MnNsMHF1c3ZicmV6In0.wcM_OYGTxxDZq8D8tnfKxA"
        initialViewState={{
          latitude: 43,
          longitude: 15.5,
          zoom: 6,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        attributionControl={false}
        onClick={handleClick}
        onLoad={() => {
          if (regions.length > 0) {
            mapRef.current?.fitBounds(boundsFor(regions[0]), {
              padding: 40,
              duration: 0,
            })
          }
        }}
        cursor={loading ? 'wait' : 'crosshair'}
      >
        {mode === 'route' && start && (
          <Marker
            longitude={start[0]}
            latitude={start[1]}
            color="green"
            anchor="bottom"
          />
        )}
        {mode === 'reroute' && boat && (
          <Marker
            longitude={boat[0]}
            latitude={boat[1]}
            color="green"
            anchor="bottom"
          />
        )}
        {end && (
          <Marker
            longitude={end[0]}
            latitude={end[1]}
            color="red"
            anchor="bottom"
          />
        )}

        {/* Sea override zones */}
        {overrides.length > 0 && (
          <Source
            id="overrides-source"
            type="geojson"
            data={{ type: 'FeatureCollection', features: overrides }}
          >
            <Layer
              id="overrides-fill"
              type="fill"
              paint={{ 'fill-color': '#3b82f6', 'fill-opacity': 0.15 }}
            />
            <Layer
              id="overrides-outline"
              type="line"
              paint={{
                'line-color': '#2563eb',
                'line-width': 1.5,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}

        <Source id="drawing-source" type="geojson" data={drawing}>
          {/* Previous route (replaced by a reroute): thin grey dashes */}
          <Layer
            id="previous-layer"
            type="line"
            filter={kindIs('previous')}
            paint={{
              'line-color': '#9ca3af',
              'line-width': 2,
              'line-dasharray': [1, 2],
            }}
          />
          {/* Boat track: recorded fixes; unsent ones hollow */}
          <Layer
            id="track-layer"
            type="line"
            filter={kindIs('track')}
            paint={{ 'line-color': '#6b7280', 'line-width': 1.5 }}
          />
          <Layer
            id="fix-layer"
            type="circle"
            filter={kindIs('fix')}
            paint={{
              'circle-radius': 4,
              'circle-color': [
                'case',
                ['get', 'sent'],
                '#6b7280',
                'white',
              ] as any,
              'circle-stroke-color': '#6b7280',
              'circle-stroke-width': 1.5,
            }}
          />
          {/* Fresh route: blue. Kept previous route (on route): green */}
          <Layer
            id="route-layer"
            type="line"
            filter={kindIs('route')}
            paint={{ 'line-color': '#3b82f6', 'line-width': 3 }}
          />
          <Layer
            id="kept-layer"
            type="line"
            filter={kindIs('kept')}
            paint={{ 'line-color': '#16a34a', 'line-width': 3 }}
          />
          {/* Land legs (pin -> shore, fix -> route): orange dashes */}
          <Layer
            id="land-leg-layer"
            type="line"
            filter={kindIs('land')}
            paint={{
              'line-color': '#f59e0b',
              'line-width': 3,
              'line-dasharray': [1.5, 1.5],
            }}
          />
        </Source>
      </Map>

      {/* Status overlay */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          maxWidth: 'calc(100vw - 520px)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '8px 16px',
          borderRadius: 8,
          fontSize: 14,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {loading && <Loader size="xs" />}
        <span style={{ color: error ? '#e03131' : '#333' }}>{statusText}</span>
      </div>

      {/* Top-right controls */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <div
            style={{
              display: 'flex',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              borderRadius: 8,
            }}
          >
            <button
              onClick={() => switchMode('route')}
              style={{
                ...buttonStyle(mode === 'route'),
                boxShadow: 'none',
                borderRadius: '8px 0 0 8px',
              }}
            >
              Route
            </button>
            <button
              onClick={() => switchMode('reroute')}
              style={{
                ...buttonStyle(mode === 'reroute'),
                boxShadow: 'none',
                borderRadius: '0 8px 8px 0',
              }}
            >
              Reroute
            </button>
          </div>
          {mode === 'reroute' && (
            <button
              onClick={() => setRecording((r) => !r)}
              style={buttonStyle(recording)}
              title="On: clicks only add fixes to the boat's track. Off: every click adds a fix and calls /reroute"
            >
              Boat Track{recording ? ' ●' : ''}
            </button>
          )}
          {mode === 'reroute' && end && fixes.length > 0 && (
            <button
              onClick={() => sendReroute(fixes, end, answers)}
              disabled={loading}
              style={buttonStyle(false, unsent > 0)}
              title={`POST /reroute with the last ${TRACK_FIXES_SENT} fixes of the track`}
            >
              Reroute now
            </button>
          )}
          {regions.length > 1 &&
            regions.map((region) => (
              <button
                key={region.name}
                onClick={() =>
                  mapRef.current?.fitBounds(boundsFor(region), {
                    padding: 40,
                    duration: 500,
                  })
                }
                title={region.ready ? undefined : 'mesh building…'}
                style={{ ...buttonStyle(), opacity: region.ready ? 1 : 0.5 }}
              >
                {region.name}
              </button>
            ))}
          <button
            onClick={() => navigate('/sea-overrides')}
            style={buttonStyle(false, true)}
          >
            Edit Sea Zones
          </button>
          {mode === 'reroute' && fixes.length > 0 && (
            <button onClick={undo} style={buttonStyle()} title="Ctrl+Z">
              Undo fix
            </button>
          )}
          {(start ||
            end ||
            routeData ||
            baseRoute ||
            fixes.length > 0 ||
            error) && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                reset()
              }}
              style={buttonStyle()}
            >
              Reset
            </button>
          )}
        </div>

        {/* Request parameters */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '6px 12px',
            borderRadius: 8,
            fontSize: 13,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <label title="Max distance to snap a land point to the sea; blank = server default (route 1000 m, reroute 500 m), 0 = off">
            snap m{' '}
            <input
              style={inputStyle}
              placeholder="default"
              value={snapM}
              onChange={(e) => setSnapM(e.target.value)}
            />
          </label>
          {mode === 'reroute' && (
            <>
              <label title="Boat speed for the ETA and waypoint timestamps">
                speed km/h{' '}
                <input
                  style={inputStyle}
                  value={speedKmh}
                  onChange={(e) => setSpeedKmh(e.target.value)}
                />
              </label>
              <label title="Off-route tolerance; the last 3 fixes must all exceed it before rerouting">
                off-route m{' '}
                <input
                  style={inputStyle}
                  value={offRouteM}
                  onChange={(e) => setOffRouteM(e.target.value)}
                />
              </label>
              <label title="Send the current route as previous_route (on-route → kept & trimmed, green); off = always a fresh route">
                <input
                  type="checkbox"
                  checked={usePrevious}
                  onChange={(e) => setUsePrevious(e.target.checked)}
                />{' '}
                send previous route
              </label>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
